import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sidSelectStyles } from '../../../../components/Util/sidSelectStyles';
import { usePageTitle } from '../../../../layouts/PageHeaderExtra';
import { useConflittiOrario, EsitoConflitto } from '../../../../components/Util/conflittiOrario';

/*
 * Modifica di una singola occorrenza (seduta) di un corso: data, orari, impianto e maestro.
 *
 * Prima di salvare la pagina controlla la congruita': chiede al calendario gli eventi di
 * quel giorno per l'impianto e per il maestro scelti e segnala le sovrapposizioni con il
 * nuovo orario (esclusa la seduta stessa). Con un conflitto non si salva. Il backend
 * ripete comunque il controllo al salvataggio.
 *
 * La struttura sceglie chi avvisare per email: la squadra e/o il maestro assegnato
 * (utile quando subentra un maestro diverso).
 */

const api = new MyApiClient();

const getToken = () => {
	try {
		return JSON.parse(sessionStorage.getItem('userDetails') || '{}').idToken;
	} catch (e) {
		return null;
	}
};

// ISO -> campi data/ora locali (il backend lavora in Europe/Rome)
const isoToInputs = (iso) => {
	const d = iso ? new Date(iso) : null;
	if (!d || isNaN(d)) return { date: '', time: '' };
	const pad = (n) => String(n).padStart(2, '0');
	return {
		date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
		time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
	};
};

const inputsToIso = (date, time) => {
	if (!date || !time) return null;
	const d = new Date(`${date}T${time}:00`);
	return isNaN(d) ? null : d.toISOString();
};


export const OccorrenzaForm = ({ id, onAnnulla, onSalvato, onCaricato, inPannello = false }) => {

	const [evento, setEvento] = useState(null);
	const [errore, setErrore] = useState('');
	const [salvataggio, setSalvataggio] = useState(false);

	const [data, setData] = useState('');
	const [oraInizio, setOraInizio] = useState('');
	const [oraFine, setOraFine] = useState('');
	const [impiantoId, setImpiantoId] = useState('');
	const [staffId, setStaffId] = useState('');

	const [notificaSquadra, setNotificaSquadra] = useState(true);
	const [notificaMaestro, setNotificaMaestro] = useState(false);
	const [notificaMaestroToccata, setNotificaMaestroToccata] = useState(false);

	const [risorse, setRisorse] = useState({ impianti: [], maestri: [] });

	// Carica la seduta
	useEffect(() => {
		const token = getToken();
		if (!token) return;
		api.getCalendarioEventoById(token, id).then((res) => {
			if (!res || res.error) {
				setErrore(res?.error || 'Occorrenza non trovata');
				return;
			}
			setEvento(res);
			if (onCaricato) onCaricato(res);
			const inizio = isoToInputs(res.dataInizio);
			const fine = isoToInputs(res.dataFine);
			setData(inizio.date);
			setOraInizio(inizio.time);
			setOraFine(fine.time);
			setImpiantoId(res.impianto?.id || '');
			setStaffId(res.staffTecnico?.id || '');
		});
		// si ricarica solo cambiando seduta; onCaricato serve una volta sola
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// Impianti e maestri disponibili nel giorno scelto
	useEffect(() => {
		const token = getToken();
		if (!token || !data) return;
		api.getCalendarioRisorse(token, data, data).then((res) => {
			if (res && !res.error) setRisorse({ impianti: res.impianti || [], maestri: res.maestri || [] });
		});
	}, [data]);

	const nuovoInizio = inputsToIso(data, oraInizio);
	const nuovaFine = inputsToIso(data, oraFine);
	const orariValidi = nuovoInizio && nuovaFine && new Date(nuovaFine) > new Date(nuovoInizio);

	const maestroCambiato = evento && staffId && staffId !== (evento.staffTecnico?.id || '');

	// Se subentra un altro maestro lo si avvisa, salvo scelta diversa della struttura
	useEffect(() => {
		if (!notificaMaestroToccata) setNotificaMaestro(!!maestroCambiato);
	}, [maestroCambiato, notificaMaestroToccata]);

	// Controllo di congruita' (uguale in tutta l'app, components/Util/conflittiOrario.js): impianto e maestro liberi nel nuovo orario
	const verifica = useConflittiOrario({ data, inizio: oraInizio, fine: oraFine, impiantoId, staffId, escludiId: evento?.id, attivo: !!evento });
	const conflitto = verifica.conflitto;
	const puoSalvare = evento && orariValidi && !verifica.inCorso && !conflitto && !salvataggio;

	const opzioniImpianti = useMemo(
		() => (risorse.impianti || []).map((r) => ({ value: r.id, label: r.nome })),
		[risorse.impianti],
	);
	const opzioniMaestri = useMemo(
		() =>
			Array.from(new Map((risorse.maestri || []).map((r) => [r.id, r])).values()).map((r) => ({
				value: r.id,
				label: r.nomeCoach || r.nome,
			})),
		[risorse.maestri],
	);
	// La risorsa attuale resta selezionabile anche se non compare fra quelle del giorno
	const conAttuale = (opzioni, valore, etichetta) =>
		valore && !opzioni.some((o) => o.value === valore) ? [{ value: valore, label: etichetta || 'Attuale' }, ...opzioni] : opzioni;
	const impiantiSelezionabili = conAttuale(opzioniImpianti, evento?.impianto?.id, evento?.impianto?.nome);
	const maestriSelezionabili = conAttuale(opzioniMaestri, evento?.staffTecnico?.id, evento?.staffTecnico?.nome);

	const salva = async () => {
		if (!puoSalvare) return;
		setErrore('');
		setSalvataggio(true);
		const payload = {
			newDataInizio: nuovoInizio,
			newDataFine: nuovaFine,
			notificaPartecipanti: notificaSquadra,
			notificaStaff: notificaMaestro,
		};
		if (impiantoId) payload.newImpiantoId = impiantoId;
		if (staffId) payload.newStaffId = staffId;
		const res = await api.rescheduleCalendarioEvento(getToken(), id, payload);
		setSalvataggio(false);
		if (res && !res.error) onSalvato(res);
		else setErrore(res?.error || 'Errore durante il salvataggio');
	};

	if (!evento) {
		return <div className="py-3">{errore ? errore : <Spinner animation="border" size="sm" />}</div>;
	}

	const modulo = (
		<>
					{!inPannello && (
						<Row className="g-4">
							<Col md={6}>
								<Form.Label>Corso</Form.Label>
								<div className="sid-readonly-value">{evento.corso?.nome || evento.titolo}</div>
							</Col>
							<Col md={6}>
								<Form.Label>Squadra</Form.Label>
								<div className="sid-readonly-value">{evento.squadra?.nome || '-'}</div>
							</Col>
						</Row>
					)}

					{!inPannello && <h5 className="sid-form-section">Nuovo orario</h5>}
					<Row className="g-4">
						<Col md={4}>
							<Form.Label htmlFor="occ-data">Data</Form.Label>
							<Form.Control id="occ-data" className="mt-2" type="date" value={data} onChange={(e) => setData(e.target.value)} />
						</Col>
						<Col md={4}>
							<Form.Label htmlFor="occ-inizio">Ora inizio</Form.Label>
							<Form.Control id="occ-inizio" className="mt-2" type="time" value={oraInizio} onChange={(e) => setOraInizio(e.target.value)} />
						</Col>
						<Col md={4}>
							<Form.Label htmlFor="occ-fine">Ora fine</Form.Label>
							<Form.Control id="occ-fine" className="mt-2" type="time" value={oraFine} onChange={(e) => setOraFine(e.target.value)} />
						</Col>
						{!orariValidi && (
							<Col md={12}>
								<span className="sid-check sid-check--ko">L'ora di fine deve essere successiva all'ora di inizio</span>
							</Col>
						)}
						<Col md={6}>
							<Form.Label>Impianto</Form.Label>
							<Select
								className="mt-2"
								styles={sidSelectStyles}
								options={impiantiSelezionabili}
								value={impiantiSelezionabili.find((o) => o.value === impiantoId) || null}
								onChange={(o) => setImpiantoId(o?.value || '')}
							/>
							<div className="mt-2">{impiantoId && <EsitoConflitto risultato={verifica.impianto} inCorso={verifica.inCorso} nome="Impianto" nascondiOk={verifica.conflitto} />}</div>
						</Col>
						<Col md={6}>
							<Form.Label>Maestro</Form.Label>
							<Select
								className="mt-2"
								styles={sidSelectStyles}
								options={maestriSelezionabili}
								value={maestriSelezionabili.find((o) => o.value === staffId) || null}
								onChange={(o) => setStaffId(o?.value || '')}
							/>
							<div className="mt-2">{staffId && <EsitoConflitto risultato={verifica.maestro} inCorso={verifica.inCorso} nome="Maestro" nascondiOk={verifica.conflitto} />}</div>
						</Col>
					</Row>

					<h5 className="sid-form-section">Avvisi email</h5>
					<div className="sid-switch-inline">
						<div className="sid-switch-row">
							<Form.Check
								type="switch"
								id="occ-notifica-squadra"
								label="Avvisa tutta la squadra"
								checked={notificaSquadra}
								onChange={(e) => setNotificaSquadra(e.target.checked)}
							/>
						</div>
						<div className="sid-switch-row">
							<Form.Check
								type="switch"
								id="occ-notifica-maestro"
								label={maestroCambiato ? 'Avvisa il maestro che subentra' : 'Avvisa il maestro'}
								checked={notificaMaestro}
								onChange={(e) => {
									setNotificaMaestroToccata(true);
									setNotificaMaestro(e.target.checked);
								}}
							/>
						</div>
					</div>

					{errore && <div className="sid-check sid-check--ko mt-4">{errore}</div>}
		</>
	);

	const pulsanti = (
		<div className="d-flex justify-content-end gap-2 mt-4">
			<Button size="sm" className="btn btn-dark light sid-btn-compact" onClick={onAnnulla} disabled={salvataggio}>
				Annulla
			</Button>
			<Button size="sm" className="btn btn-primary sid-btn-compact" onClick={salva} disabled={!puoSalvare}>
				{salvataggio ? 'Salvataggio…' : 'Salva'}
			</Button>
		</div>
	);

	if (inPannello) {
		return (
			<div className="sid-occ-panel">
				{modulo}
				{pulsanti}
			</div>
		);
	}

	return (
		<>
			<Card>
				<Card.Body className="sid-card-body--spaced">{modulo}</Card.Body>
			</Card>
			{pulsanti}
		</>
	);
};

// Pagina a se' (link diretto): stesso modulo, poi si torna da dove si e' arrivati
export const OccorrenzaCorso = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const [titolo, setTitolo] = useState(null);
	usePageTitle(titolo ? `Modifica occorrenza: ${titolo}` : null);
	const tornaIndietro = () => (location.state?.ritorno ? navigate(location.state.ritorno) : navigate(-1));
	return <OccorrenzaForm id={id} onAnnulla={tornaIndietro} onSalvato={tornaIndietro} onCaricato={(e) => setTitolo(e.titolo)} />;
};

export default OccorrenzaCorso;
